"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

type NavItem = {
  label: string;
  href: string;
};

const NAV_ITEMS: NavItem[] = [
  { label: "How it Works", href: "/#how-it-works" },
  { label: "Features", href: "/#features" },
  { label: "Blog", href: "/blog" },
  { label: "FAQ", href: "/#faq" },
];

const CTA_HREF = "/#download";

export default function Header() {
  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("");

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (pathname !== "/") return;

    const readHash = () => {
      setActiveHash(window.location.hash || "");
    };

    readHash();
    window.addEventListener("hashchange", readHash);

    return () => {
      window.removeEventListener("hashchange", readHash);
    };
  }, [pathname]);

  const headerOffset = useMemo(() => (isScrolled ? 92 : 102), [isScrolled]);

  const isItemActive = (href: string) => {
    if (href === "/blog") return pathname === "/blog";
    if (!href.startsWith("/#")) return pathname === href;
    return pathname === "/" && activeHash === href.slice(1);
  };

  const handleNavClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    setMobileMenuOpen(false);

    if (pathname === "/" && href.startsWith("/#")) {
      setActiveHash(href.slice(1));
    } else if (!href.startsWith("/#")) {
      setActiveHash("");
    }

    handleAnchorNavigation(event, href);
  };

  const handleAnchorNavigation = (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (!href.startsWith("/#") || pathname !== "/") return;

    const targetId = href.slice(2);
    const targetEl = document.getElementById(targetId);
    if (!targetEl) return;

    event.preventDefault();
    setMobileMenuOpen(false);

    const top =
      targetEl.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: "smooth",
    });
    // Keep same-page navigation clean without exposing hash fragments in URL.
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
    setActiveHash(`#${targetId}`);
  };

  return (
    <header className={`pb-header-shell ${isScrolled ? "is-scrolled" : ""}`}>
      <div className="pb-header-fixed">
        <div className="pb-header-wrap">
          <div className="pb-header-surface">
            <div className="pb-header-row">
              <Link
                href="/"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setActiveHash("");
                }}
                className="pb-header-brand pb-header-focus"
              >
                <span className="pb-header-logo-wrap" aria-hidden="true">
                  <Image
                    src="/logo-mark.svg"
                    alt="PitchBuddy logo"
                    width={20}
                    height={20}
                    priority
                  />
                </span>
                <span className="pb-header-brand-text">PitchBuddy</span>
              </Link>

              <nav className="pb-header-nav" aria-label="Primary navigation">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.label}
                    href={item.href}
                    onClick={(event) => handleNavClick(event, item.href)}
                    className={`pb-header-nav-link pb-header-focus ${isItemActive(item.href) ? "is-active" : ""}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>

              <div className="pb-header-cta">
                <Link
                  href={CTA_HREF}
                  onClick={(event) => handleNavClick(event, CTA_HREF)}
                  className="pb-header-cta-link pb-header-focus"
                  aria-label="Download PitchBuddy"
                >
                  Download Now
                </Link>
              </div>

              <button
                type="button"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                className="pb-header-menu-btn pb-header-focus"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-nav-panel"
                aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              >
                <span className="sr-only">Toggle navigation menu</span>
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  {mobileMenuOpen ? (
                    <path
                      d="M6 6L18 18M18 6L6 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  ) : (
                    <path
                      d="M4 7H20M4 12H20M4 17H20"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          <div
            id="mobile-nav-panel"
            className={`pb-header-mobile-panel ${mobileMenuOpen ? "is-open" : ""}`}
          >
            <div className="pb-header-mobile-surface">
              <nav className="pb-header-mobile-nav" aria-label="Mobile primary navigation">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={`mobile-${item.label}`}
                    href={item.href}
                    onClick={(event) => handleNavClick(event, item.href)}
                    className={`pb-header-mobile-link pb-header-focus ${isItemActive(item.href) ? "is-active" : ""}`}
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  href={CTA_HREF}
                  onClick={(event) => handleNavClick(event, CTA_HREF)}
                  className="pb-header-mobile-cta pb-header-cta-link pb-header-focus"
                  aria-label="Download PitchBuddy"
                >
                  Download Now
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <div aria-hidden="true" className="pb-header-spacer" />
    </header>
  );
}

